/**
 * DOM HUD: squad count, enemy M for the block in range, weapon, boss bar, per-object HP bars,
 * win/lose overlay, debug line. Crisp text, zero engine cost (plan 02). Also the ?debug=1 perf
 * overlay (fps/backend/scale/meshes).
 */
import { HP_BAR_POOL, type ScreenBar } from './hpbars';

export interface Hud {
  setSquad(n: number): void;
  /**
   * Enemy headcount for the block inside ENEMY_LOOKAHEAD metres (plan 04 P1.5); null hides the
   * readout. The last written value is remembered ONLY to skip redundant DOM writes; the probe's
   * readback (`debugEnemyM`) goes to the DOM itself, so a dropped write cannot agree with the sim.
   */
  setEnemy(m: number | null, at?: { left: number; top: number }): void;
  /** Weapon label plus one ▲ per rate pip (plan 11); `gained` flashes it green (true) or red (false). */
  setWeapon(label: string, pips?: number, gained?: boolean): void;
  setBoss(visible: boolean, fraction?: number): void;
  /**
   * Win/lose overlay. `opts.levelName` prints the level that was cleared (plan 04 P1.7 wires a
   * second level, so "BRIDGE SECURED" cannot be hardcoded); `opts.nextLevel` reveals the CONTINUE
   * button — the whole level-flow UI the plan allows (no menu system). `opts.atFirstLevel`
   * (plan 21) picks the restart button's label: TRY AGAIN when the target is the current level
   * (the in-place L1 retry), START OVER when the button jumps back to L1.
   */
  showEnd(won: boolean, n: number, seconds: number, opts?: { levelName?: string; nextLevel?: boolean; atFirstLevel?: boolean }): void;
  hideEnd(): void;
  /**
   * Plan 17 D2: the level intro card (`#level-intro`) — eyebrow `LEVEL ${levelNo}` + uppercase
   * operation name, shown on the real-user START click (main.ts, its LAST statement) and gone
   * again by its own CSS animation (fade-in → hold → fade-out, `forwards` hidden end-state).
   * `#level-intro` is resolved LAZILY at call time — existing HUD tests need no new fixture, and
   * a missing card is a silent no-op (it must never break the start gate). Re-show re-triggers
   * the animation from the top.
   */
  showLevelIntro(levelNo: number, name: string): void;
  debug(line: string): void;
  /**
   * What the HUD is ACTUALLY displaying for M, read back off the DOM (null = hidden) — never the
   * argument last handed to `setEnemy`. The probe compares this with an independent recount of the
   * sim, so it has to be able to fail when a write is dropped, lands on the wrong element, or the
   * readout is hidden (plan 04 fix round).
   */
  debugEnemyM(): number | null;
  /** Plan 09: place the pooled HP bars (already projected, nearest first); unused ones hide. */
  setHpBars(bars: ScreenBar[]): void;
  /** The bars ACTUALLY shown, read back off the DOM in pool order (frac to 3 dp) — P1.5 rule. */
  debugHpBars(): { key: string; frac: number }[];
}

export type HudWithRestart = Hud & {
  onRestart(cb: () => void): void;
  onNextLevel(cb: () => void): void;
  /** Wire the speaker button to the audio director (click toggles; icon/aria follow any toggle, incl. the M key). */
  bindMute(audio: { toggleMute(): void; readonly muted: boolean; onMuteChange(cb: (m: boolean) => void): void }): void;
};

// Inline SVG (no emoji font dependency): speaker body + waves, or an X when muted.
const SVG = (extra: string): string =>
  `<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9z" stroke="none"/>${extra}</svg>`;
const ICON_ON = SVG('<path d="M16 9a4 4 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11" fill="none"/>');
const ICON_MUTED = SVG('<path d="M16 9l5 6M21 9l-5 6" fill="none"/>');

/** Plan 16: the end-screen button rule — CONTINUE only on a win with a following level; the
 *  restart button only at the last level or on a loss (a loss with no button would softlock).
 *  Plan 21 changed WHERE the restart button goes (final win and loss-beyond-L1 jump back to L1),
 *  never whether it shows — visibility is still this rule, pinned by the 2×2 test below. */
export function endScreenButtons(won: boolean, hasNextLevel: boolean): { continueButton: boolean; runAgain: boolean } {
  if (won && hasNextLevel) return { continueButton: true, runAgain: false };
  return { continueButton: false, runAgain: true };
}

/**
 * Plan 21: the restart button's LABEL (visibility is endScreenButtons above). START OVER when
 * the button jumps — the final-level win loops the run back to L1, and a death beyond the first
 * level restarts the run at L1; TRY AGAIN only when the target IS the current level (the L1
 * in-place retry, the plan-16 UX). `won && hasNextLevel` is unreachable (endScreenButtons hides
 * the button there); it returns TRY AGAIN to stay a total function. Degenerate 1-level note
 * (impl-gate opus P2-3): a final win there has target === current (an in-place restart) yet
 * labels START OVER — outcome-identical, unreachable while SHIPPED_LEVELS has 3 levels; the
 * label/action parity test (hud.test.ts) scopes to the reachable cells.
 */
export function endScreenRestartLabel(won: boolean, hasNextLevel: boolean, atFirstLevel: boolean): string {
  if (won) return hasNextLevel ? 'TRY AGAIN' : 'START OVER';
  return atFirstLevel ? 'TRY AGAIN' : 'START OVER';
}

export function createHud(): HudWithRestart {
  const count = document.getElementById('squad-count')!;
  const label = document.getElementById('squad-label')!;
  const enemyCount = document.getElementById('enemy-count')!;
  const enemyLabel = document.getElementById('enemy-label')!;
  const weapon = document.getElementById('weapon')!;
  const bossBar = document.getElementById('boss-bar')!;
  const bossFill = bossBar.querySelector<HTMLElement>('.fill')!;
  const overlay = document.getElementById('overlay')!;
  const title = overlay.querySelector<HTMLElement>('.title')!;
  const sub = overlay.querySelector<HTMLElement>('.sub')!;
  const restart = document.getElementById('restart')!;
  const nextLevel = document.getElementById('next-level')!;
  const muteBtn = document.getElementById('mute')!;
  const debugEl = document.getElementById('debug')!;
  const debugOn = new URLSearchParams(location.search).get('debug') === '1';
  const hpRoot = document.getElementById('hp-bars')!;
  const hpPool = Array.from({ length: HP_BAR_POOL }, () => {
    const el = document.createElement('div');
    el.className = 'hpbar';
    const fill = document.createElement('div');
    fill.className = 'fill';
    el.appendChild(fill);
    hpRoot.appendChild(el);
    return { el, fill };
  });
  if (debugOn) debugEl.style.display = 'block';

  let lastN = -1;
  // `undefined` = "nothing written yet": the first setEnemy(null) still paints the hidden state,
  // which is what debugEnemyM() reads back (an untouched element has neither inline display nor
  // textContent, and would otherwise read as a visible 0).
  let displayedEnemyM: number | null | undefined = undefined;
  return {
    setSquad(n: number): void {
      if (n === lastN) return;
      lastN = n;
      count.textContent = String(n);
      label.textContent = n === 0 ? 'wiped' : 'squad';
      count.style.color = n <= 10 ? '#ff9a8a' : '#e8e6e0';
    },
    setEnemy(m: number | null, at?: { left: number; top: number }): void {
      // Sits over the block (`at` = its projected anchor, CSS px): label on the anchor, count above.
      if (m !== null && at) {
        enemyLabel.style.transform = `translate(${at.left.toFixed(1)}px, ${at.top.toFixed(1)}px) translate(-50%, -100%)`;
        enemyCount.style.transform = `translate(${at.left.toFixed(1)}px, ${(at.top - 14).toFixed(1)}px) translate(-50%, -100%)`;
      }
      if (m === displayedEnemyM) return;
      displayedEnemyM = m;
      if (m === null) {
        enemyCount.style.display = 'none';
        enemyLabel.style.display = 'none';
        return;
      }
      enemyCount.style.display = 'block';
      enemyLabel.style.display = 'block';
      enemyCount.textContent = String(m);
      enemyCount.style.color = m <= 5 ? '#ffd257' : '#ffb0a4';
    },
    debugEnemyM(): number | null {
      if (enemyCount.style.display === 'none') return null;
      const text = enemyCount.textContent?.trim() ?? '';
      if (text === '') return null; // never painted: Number('') would be a fake 0
      const m = Number(text);
      return Number.isFinite(m) ? m : null;
    },
    setHpBars(bars: ScreenBar[]): void {
      hpPool.forEach(({ el, fill }, i) => {
        const bar = bars[i];
        el.style.display = bar ? 'block' : 'none';
        if (!bar) return;
        el.dataset.key = bar.key;
        el.style.transform = `translate(${bar.left.toFixed(1)}px, ${bar.top.toFixed(1)}px)`;
        el.style.width = `${bar.width.toFixed(1)}px`;
        fill.style.transform = `scaleX(${bar.frac})`;
        fill.style.background = bar.explosive ? '#e8643c' : '#ffd257';
      });
    },
    debugHpBars(): { key: string; frac: number }[] {
      return hpPool
        .filter(({ el }) => el.style.display === 'block')
        .map(({ el, fill }) => ({
          key: el.dataset.key ?? '',
          frac: Math.round(Number(/scaleX\(([^)]+)\)/.exec(fill.style.transform)?.[1] ?? NaN) * 1000) / 1000,
        }));
    },
    setWeapon(text: string, pips = 0, gained?: boolean): void {
      weapon.textContent = pips > 0 ? `${text} ${'▲'.repeat(pips)}` : text;
      if (gained === undefined) return;
      const flash = gained ? 'rgba(111, 227, 154, 0.8)' : 'rgba(255, 122, 107, 0.8)';
      weapon.animate([{ background: flash }, { background: 'rgba(0, 0, 0, 0.35)' }], { duration: 400 });
    },
    setBoss(visible: boolean, fraction = 1): void {
      bossBar.style.display = visible ? 'block' : 'none';
      bossFill.style.width = `${Math.max(0, Math.min(1, fraction)) * 100}%`;
    },
    showEnd(
      won: boolean,
      n: number,
      seconds: number,
      opts: { levelName?: string; nextLevel?: boolean; atFirstLevel?: boolean } = {},
    ): void {
      title.textContent = won ? `${(opts.levelName ?? 'bridge').toUpperCase()} SECURED` : 'SQUAD WIPED';
      sub.textContent = won
        ? `${n} soldiers walked off the far side — ${seconds.toFixed(1)} s.`
        : `The squad held for ${seconds.toFixed(1)} s. Watch the gate math, dodge the slams.`;
      // CONTINUE only ever belongs to a win with a level actually following; the restart button
      // covers the last-level win and every loss (a loss with no button would softlock). Both repaint every call.
      const buttons = endScreenButtons(won, !!opts.nextLevel);
      nextLevel.style.display = buttons.continueButton ? 'inline-block' : 'none';
      restart.style.display = buttons.runAgain ? 'inline-block' : 'none';
      // Plan 21: the label follows the target level — START OVER when the button jumps (final
      // win loops to L1; a death beyond L1 restarts the run at L1), TRY AGAIN when the target is
      // the current level (the in-place L1 retry). Pure rule, pinned in hud.test.ts.
      restart.textContent = endScreenRestartLabel(won, !!opts.nextLevel, !!opts.atFirstLevel);
      overlay.style.display = 'flex';
      // Keyboard parity (mirrors hud.bindMute's blur rationale, inverted): focusing the one visible
      // button lets Enter/Space activate it. showEnd only runs at a phase end, so the overlay is
      // always being shown to a user here — and exactly one button is visible (endScreenButtons).
      (buttons.continueButton ? nextLevel : restart).focus();
    },
    hideEnd(): void {
      overlay.style.display = 'none';
    },
    showLevelIntro(levelNo: number, name: string): void {
      // Lazy resolve (plan 17 D2): the card is optional furniture — no fixture demand on the
      // existing HUD tests, and a missing element cannot take the start seam down.
      const card = document.getElementById('level-intro');
      if (!card) return;
      const eyebrow = card.querySelector<HTMLElement>('.eyebrow');
      const titleEl = card.querySelector<HTMLElement>('.title');
      if (eyebrow) eyebrow.textContent = `LEVEL ${levelNo}`;
      if (titleEl) titleEl.textContent = name.toUpperCase();
      // Re-trigger the CSS animation on re-show: class off → forced reflow → class on. Without
      // the reflow the re-add of an already-settled animation is a no-op and the card stays hidden.
      card.classList.remove('play');
      void card.offsetWidth;
      card.classList.add('play');
    },
    debug(line: string): void {
      if (debugOn) debugEl.textContent = line;
    },
    bindMute(audio): void {
      const paint = (muted: boolean): void => {
        muteBtn.setAttribute('aria-pressed', String(muted));
        muteBtn.setAttribute('aria-label', muted ? 'Unmute sound' : 'Mute sound');
        muteBtn.innerHTML = muted ? ICON_MUTED : ICON_ON;
      };
      paint(audio.muted);
      audio.onMuteChange(paint);
      muteBtn.addEventListener('click', () => {
        audio.toggleMute();
        muteBtn.blur(); // keep Space/Enter from re-toggling the focused button mid-play
      });
    },
    onRestart(callback: () => void): void {
      restart.addEventListener('click', () => callback());
    },
    onNextLevel(callback: () => void): void {
      nextLevel.addEventListener('click', () => callback());
    },
  };
}

